import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import {
  Box,
  Tooltip,
  Typography,
  CircularProgress,
  BoxProps,
} from '@mui/material';
import { useSparkOutputReferences } from 'hooks/SparkBuilder/sparkStepReferences';
import { useParams } from 'react-router-dom';
import { SparkImageLoader } from 'sharedComponents/UI';
import { SparkReferenceValue } from 'types/SparkStepReference';
import { WarningStatusIcon } from 'components/SVG';
import { UUID_PATTERN } from '../regex';

// Function to extract UUID from a reference string
const extractUUID = (referenceString: string): string | null => {
  // Use the centralized UUID pattern
  const match = referenceString.match(UUID_PATTERN);

  if (match && match[1]) {
    return match[1]; // Return the captured UUID
  }

  return null;
};

// Function to normalize reference IDs - strip curly braces if present
const normalizeReferenceId = (id: string): string => {
  // If it starts with {{ and ends with }}, strip them
  if (id.startsWith('{{') && id.endsWith('}}')) {
    return id.substring(2, id.length - 2);
  }
  return id;
};

const ReferenceComponent = React.memo((props: any) => {
  const { id: sparkID } = useParams();
  const isSelected = !!props.selected;

  // Create a unique instance ID for this specific component instance
  const [instanceId] = React.useState(
    `ref-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
  );

  // Use the reference hook to get reference data
  const { allReferencesIdMap, isLoading: isLoadingReferences } =
    useSparkOutputReferences(sparkID || '');

  // Common styles for all reference types
  const commonStyles: BoxProps['sx'] = {
    display: 'inline-flex',
    alignItems: 'center',
    height: '24px',
    borderRadius: '4px',
    margin: '0 2px',
    cursor: 'pointer',
    border: isSelected
      ? (theme) => `2px solid ${theme.palette.primary.main}`
      : '1px solid #eee',
    '&:hover': {
      outline: (theme) => `1px solid ${theme.palette.primary.main}`,
    },
    // Ensure stability in the DOM
    position: 'relative',
    zIndex: 1,
    // Make sure we don't interfere with text selection and cursor positioning
    userSelect: 'none',
    caretColor: 'transparent', // Hide cursor inside the component
  };

  // Get the reference ID from node attributes
  const referenceID = props.node.attrs.id;
  console.log('referenceID', JSON.stringify(referenceID));

  // Normalize the reference ID (remove curly braces if present)
  const normalizedReferenceID = normalizeReferenceId(referenceID);

  // Extract UUID if present in the reference ID
  const extractedUUID = extractUUID(normalizedReferenceID);

  // Try different formats, prioritizing formats with resource prefixes
  const withBraces = `{{${normalizedReferenceID}}}`;

  // Check if already contains inputs. or steps. prefix
  const hasResourcePrefix = normalizedReferenceID.match(
    /^(inputs|steps|input\.file)\./
  );

  // Check for function call pattern (like askMemory)
  const hasFunctionCall =
    normalizedReferenceID.includes('(') && normalizedReferenceID.includes(')');

  // Possible formats to try (in priority order)
  const formats = [
    // Original reference ID as is
    referenceID,

    // With braces (if needed)
    referenceID !== withBraces ? withBraces : null,

    // If it has a resource prefix, try with braces
    hasResourcePrefix ? withBraces : null,

    // Try adding inputs. prefix if missing
    !hasResourcePrefix && !hasFunctionCall
      ? `{{inputs.${normalizedReferenceID}}}`
      : null,

    // Try adding steps. prefix if missing
    !hasResourcePrefix && !hasFunctionCall
      ? `{{steps.${normalizedReferenceID}}}`
      : null,

    // For complex function expressions, try to find by UUID
    extractedUUID ? `{{askMemory("","",0.5).inputs.${extractedUUID}}}` : null,
    extractedUUID ? `{{inputs.file.${extractedUUID}}}` : null,
    extractedUUID ? `{{inputs.${extractedUUID}}}` : null,

    // Finally try without any prefix or with just braces
    normalizedReferenceID,
  ].filter(Boolean) as string[]; // Remove nulls

  // Try all possible formats to find matching reference
  let referenceValue: SparkReferenceValue | null = null;
  console.log('format', JSON.stringify({ formats, allReferencesIdMap }));

  // First, try direct lookups using all formats
  for (const format of formats) {
    if (allReferencesIdMap && allReferencesIdMap[format]) {
      referenceValue = allReferencesIdMap[format];
      break;
    }
  }

  // If not found and we have a UUID, try finding any key in the map that contains this UUID
  if (!referenceValue && extractedUUID && allReferencesIdMap) {
    const allKeys = Object.keys(allReferencesIdMap);
    for (const key of allKeys) {
      if (key.includes(extractedUUID)) {
        referenceValue = allReferencesIdMap[key];
        break;
      }
    }
  }

  // Determine if references are missing
  const missingReferences = React.useMemo(() => {
    if (isLoadingReferences) {
      return false;
    }
    return !referenceValue;
  }, [isLoadingReferences, referenceValue]);

  // Render loading state with same styling as reference error
  if (isLoadingReferences) {
    return (
      <NodeViewWrapper
        data-reference-id={referenceID}
        data-instance-id={instanceId}
        className='reference-component reference-loading'
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          marginRight: '1px',
        }}
      >
        <Tooltip title='Loading reference data...' arrow placement='top'>
          <Box
            component='span'
            sx={{
              ...commonStyles,
              background: '#fff',
            }}
          >
            {/* Icon with same positioning as valid references */}
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                margin: '0',
                padding: '4px',
                backgroundColor: '#fff',
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              }}
            >
              <CircularProgress
                size={16}
                thickness={4}
                sx={{ color: '#6268AF' }} // Purple color to match your theme
              />
            </Box>

            <Box
              component='span'
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {/* Text with same styling as valid references */}
              <Typography
                component='span'
                variant='body2'
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.2,
                  fontWeight: 600,
                  padding: '0 8px',
                  backgroundColor: '#fff',
                }}
              >
                Loading...
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </NodeViewWrapper>
    );
  }

  // Render error state for missing reference
  if (missingReferences || !referenceValue) {
    return (
      <NodeViewWrapper
        data-reference-id={referenceID}
        data-instance-id={instanceId}
        className='reference-component reference-error'
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          marginRight: '1px',
        }}
      >
        <Tooltip title='Reference could not be resolved' arrow placement='top'>
          <Box
            component='span'
            sx={{
              ...commonStyles,
              background: '#fff',
            }}
          >
            {/* Icon with same positioning as valid references */}
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                margin: '0',
                padding: '4px',
                backgroundColor: '#fff',
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              }}
            >
              <WarningStatusIcon width='16' height='16' />
            </Box>

            <Box
              component='span'
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {/* Text with same styling as valid references */}
              <Typography
                component='span'
                variant='body2'
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.2,
                  fontWeight: 600,
                  padding: '0 8px',
                  backgroundColor: '#fff',
                }}
              >
                Reference error
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </NodeViewWrapper>
    );
  }

  // Use the resolved value if available
  const { key: displayKey, value: displayValue, iconURL } = referenceValue;

  // Render the reference with the proper styling
  return (
    <NodeViewWrapper
      data-reference-id={referenceID}
      data-instance-id={instanceId}
      className='reference-component'
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        // Add a specific margin-right to ensure space for cursor after the reference
        marginRight: '1px',
      }}
    >
      <Tooltip title={displayValue} arrow placement='top'>
        <Box
          component='span'
          sx={{
            ...commonStyles,
            background: '#fff',
          }}
        >
          {/* Icon with white background */}
          {iconURL && (
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                margin: '0',
                padding: '4px',
                backgroundColor: '#fff',
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              }}
              width='16px'
              height='16px'
            >
              <SparkImageLoader
                src={iconURL}
                alt='reference-icon'
                width='16px'
                height='16px'
              />
            </Box>
          )}

          <Box
            component='span'
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            {/* Label with purple background */}
            {displayKey && (
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '100%',
                  backgroundColor: (theme) => theme.palette.primary.light,
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '0 8px',
                }}
              >
                {displayKey}
              </Box>
            )}

            {/* Value with white background */}
            <Typography
              component='span'
              variant='body2'
              sx={{
                fontSize: '13px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '120px',
                padding: '0 8px',
                backgroundColor: '#fff',
              }}
            >
              {displayValue}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </NodeViewWrapper>
  );
});

export default ReferenceComponent;
